import React, { useState } from "react";
import { FloatButton } from "antd";
import styles from "./style.module.scss";
import VLPopover from "../VLPopover";
import VLFloatButton from "../Buttons/VLFloatButton";
import MultiSelect from "views/uikit/FormFields/SelectList";
import { ActionItem } from "views/pages/DataPage/CardsView/DataList";

export interface VLFloatGroupProps {
  actionList: ActionItem[];
  placement?: "topLeft" | "topRight" | "bottomLeft" | "bottomRight";
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
  buttonSize?: "medium" | "large";
  onOpenChange?: (open: boolean) => void;
}

const VLFloatGroup: React.FC<VLFloatGroupProps> = ({
  actionList,
  placement = "topRight",
  tooltipPlacement = "top",
  buttonSize = "medium",
  onOpenChange,
}) => {
  // Track which popover is open to control tooltip visibility
  const [openPopoverIndex, setOpenPopoverIndex] = useState<number | null>(null);

  const [selectOpen, setSelectOpen] = useState(true);

  // The popover stops re-rendering its content once closed, so the (portaled)
  // select dropdown must be closed first, otherwise it stays open on screen
  const closeSelectThenPopover = () => {
    setSelectOpen(false);

    //close popover after dropdown is closed
    setTimeout(() => {
      setOpenPopoverIndex(null);
      onOpenChange?.(false);
    }, 0);
  };

  const handlePopoverOpenChange = (open: boolean, index: number) => {
    if (!open) {
      closeSelectThenPopover();
      return;
    }
    setOpenPopoverIndex(index);
    onOpenChange?.(true);
  };

  const handleOnChange = (value: string[], item: ActionItem) => {
    item.subAction?.onChange?.(value);
    closeSelectThenPopover();
  };

  return (
    <FloatButton.Group
      type="primary"
      shape="square"
      className={styles.actionList}
      data-testid="vl-float-group"
    >
      {actionList.map((item: ActionItem, actionIndex: number) => (
        <div
          key={actionIndex}
          className={styles.actionItem}
          data-testid={`vl-float-group-item-${actionIndex}`}
          onClick={(e) => e.stopPropagation()}
        >
          <VLPopover
            afterOpenChange={(open) => setSelectOpen(open)}
            placement={tooltipPlacement}
            trigger="click"
            open={openPopoverIndex === actionIndex}
            onOpenChange={(open) => handlePopoverOpenChange(open, actionIndex)}
            content={
              item.subAction ? (
                <div
                  className={styles.multiSelectDropdown}
                  data-testid="vl-float-group-multiselect-wrapper"
                  onClick={(e) => e.stopPropagation()} // clicks inside dropdown
                >
                  <MultiSelect
                    mode="multiple"
                    options={item.subAction.options}
                    placement={placement}
                    placeholder="Please select"
                    onChange={(value) => handleOnChange(value, item)}
                    open={selectOpen}
                    onDropdownVisibleChange={(open) => setSelectOpen(open)}
                  />
                </div>
              ) : undefined
            }
          >
            <VLFloatButton
              icon={item.action.icon}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                // Only trigger main action if there is no subAction
                if (!item.subAction) {
                  e.stopPropagation();
                  item.action.onClick?.(e);
                }
              }}
              color={item.action.color}
              tooltip={openPopoverIndex !== actionIndex ? item.tooltip : ""}
              tooltipPlacement={tooltipPlacement}
              buttonSize={buttonSize}
            />
          </VLPopover>
        </div>
      ))}
    </FloatButton.Group>
  );
};

export default VLFloatGroup;
